import { Tooltip } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Link } from 'react-router-dom';

import { IPresetConflict } from '~/src/dash/apiV2/targeting/targetingPresets';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  getComparator,
  ICustomRenderCellJSXProps,
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { borderRadius, Col, Cols, spacing, Span, Text } from '~/src/dash/publicaUI';
import { getItemRoute, IItemRouteID } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  TargetingPresetParameters,
  TargetingPresetParamKey,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import { ITargetingConflictTableConfig } from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/TargetingConflictsDrawer/config';

const CustomNameRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { customProps } = useListViewTableStore();
  const { session, user } = useBaseStore();
  return (
    <Link
      to={`${getItemRoute(customProps.type, props.row.ID as IItemRouteID, session, user)}`}
      target='_blank'
      style={{
        color: 'inherit',
        textDecoration: 'inherit',
      }}
    >
      <Tooltip title={value} placement='top-start'>
        <div>
          <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
            <Col
              style={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              <Text
                small
                bold
                color={publicaColors.lightBlue}
                style={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {value}
              </Text>
            </Col>
            <Col displayFlex style={{ paddingBottom: '1px' }}>
              <IconV2 name='externalLink' small color={publicaColors.lightBlue} />
            </Col>
          </Cols>
        </div>
      </Tooltip>
    </Link>
  );
};

interface IConflictTagProps {
  paramKey: TargetingPresetParamKey;
}

const ConflictTag = ({ paramKey }: IConflictTagProps) => {
  if (TargetingPresetParameters[paramKey]) {
    return (
      <Span
        style={{
          padding: '2px 10px',
          backgroundColor: publicaColors.pink,
          borderRadius: borderRadius.small,
          whiteSpace: 'nowrap',
        }}
      >
        {TargetingPresetParameters[paramKey].label}
      </Span>
    );
  } else {
    return <></>;
  }
};

const ConflictsRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  return (
    <Text small>
      {!isEmpty(value as TargetingPresetParamKey[]) &&
        (value as TargetingPresetParamKey[]).map((paramKey) => <ConflictTag paramKey={paramKey} />)}
    </Text>
  );
};

const generateDefaultColumns = (
  nameReplacement: string,
  numConflicts: number,
): IListViewTableColumns => {
  return {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: true,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Name: {
      cellJSX: CustomNameRender,
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: `${nameReplacement} (${numConflicts})`,
      requestKey: 'name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'Name',
    },
    Conflicts: {
      cellJSX: ConflictsRender,
      cellRenderType: 'right_text',
      headerRenderType: 'right_text',
      key: 'Conflicts',
      label: 'Conflicting Fields',
      requestKey: 'Conflicts',
      responseKey: 'Conflicts',
      valueType: 'array',
      disableSort: true,
    },
  };
};

export const formatDataIntoLVTRows = (
  data: Record<string, any>[],
  columns: IListViewTableColumns,
): IListViewTableRow[] => {
  return data.map((datum) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: any = datum[responseKey];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
        ...(datum?.ParentID ? { parentID: datum.ParentID } : {}),
      };
    });
    return formattedRow;
  });
};

interface IProps {
  cfg: ITargetingConflictTableConfig;
  data: IPresetConflict[];
  loadState: LoadingState;
}

export const TargetingConflictTable = ({ cfg, data, loadState }: IProps) => {
  const { cypressClassName } = cfg;

  const isMounted = useRef(false);

  const [totalRowCount, setTotalRowCount] = useState(0);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortedRows, setSortedRows] = useState<IListViewTableRow[]>([]);

  const defaultColumns = generateDefaultColumns(cfg.nameReplacement, data.length || 0);

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setTotalRowCount(data.length);
      setPage(0);
    });
  }, [data]);

  useEffect(() => {
    if (!isEmpty(data)) {
      setSortedRows(
        stableSort(rows, getComparator(order, orderBy, defaultColumns[orderBy].valueType)),
      );
    } else {
      setSortedRows([]);
    }
  }, [order, orderBy]);

  useEffect(() => {
    if (!isMounted.current) isMounted.current = true;
  });

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    });
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rows = formatDataIntoLVTRows(data, defaultColumns);
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <ListViewTable
        className={`id-name-table-${cypressClassName}`}
        loadState={loadState}
        activeColumnKeys={['Conflicts']}
        displayNameWithID
        columns={defaultColumns}
        order={order}
        orderBy={orderBy}
        page={page}
        rows={visibleRows}
        rowsPerPage={rowsPerPage}
        totalRowCount={totalRowCount}
        handleOnClickColumnHeader={handleOnClickColumnHeader}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        isExpanded={false}
        isReadAuthorized={true}
        isWriteAuthorized={true}
        key={cypressClassName}
        customProps={{ type: cfg.type }}
        backgroundColor={publicaColors.veryLightGray}
      />
    </>
  );
};
